// Velopulse R1 前端唯一的 API 入口。
// 端點與資料形狀的約定見 docs/api/velopulse-r1-frontend-contract.md（後端由另一條線實作）。
// 組織範圍：useOrgStore 會把目前組織寫入 axios 預設標頭 X-Org-Id，這裡不再重複帶。
import axios from 'axios';
import { fixtures } from './velopulseFixtures';

// 示範資料模式：僅供審閱畫面用，預設關閉。關閉時後端沒有的功能一律顯示「尚未啟用」。
export const FIXTURES_ON = process.env.REACT_APP_VELOPULSE_FIXTURES === '1';

const data = (p) => p.then((r) => r.data);

/** 端點不存在／未實作（有 HTTP status）→「尚未啟用」。網路斷線另算，勿跟尚未啟用混為一談。 */
export const isUnavailable = (err) => {
  const s = err?.response?.status;
  return [404, 501].includes(s) && !isNotFound(err);
};

/** 端點存在、但該筆資料不存在（後端以 JSON { error } 回 404；端點不存在時 Express 回的是 HTML） */
export function isNotFound(err) {
  const r = err?.response;
  return r?.status === 404 && (FIXTURES_ON || (r.data && typeof r.data === 'object' && !!r.data.error));
}

export const errorMessage = (err, fallback = '發生錯誤，請稍後再試。') => {
  if (err?.code === 'ECONNABORTED' || err?.response?.status === 504) return '連線逾時，助理可能仍在查資料，請稍後再試一次。';
  if (
    err?.response?.status === 503
    && (
      err?.response?.data?.code === 'WEB_SEARCH_UNAVAILABLE'
      || err?.response?.data?.error === '目前無法完成網路搜尋，請稍後再試。'
    )
  ) return '網路搜尋暫時無法完成，請稍後再試。';
  if (
    err?.response?.status === 503
    && err?.response?.data?.error === '目前沒有可驗證的組織知識庫資料可供查詢，因此不會用模型記憶代替。'
  ) return '目前無法使用已授權的組織知識庫，因此不會用模型記憶代替查詢結果。';
  if ([502, 503].includes(err?.response?.status)) return '服務暫時無法完成這次回應，請稍後再試。';
  // 瀏覽器 CORS／斷線時常沒有 response；萬事通長查詢逾時也常長這樣
  if (!err?.response) return '連線失敗或逾時，請稍後再試。';
  if (isUnavailable(err)) return '此功能的後端服務尚未啟用，暫時無法使用。';
  if (err?.response?.status === 403) return err?.response?.data?.error || '您在目前組織沒有此操作的權限。';
  return err?.response?.data?.error || fallback;
};

const live = {
  listOrgs: () => data(axios.get('/api/orgs')),
  getWorkbench: () => data(axios.get('/api/workbench')),
  listRecords: ({ type = 'vehicle', q, status, sort } = {}) => data(axios.get('/api/records', { params: { type, q: q || undefined, status: status || undefined, sort: sort || undefined } })),
  getRecord: (id) => data(axios.get(`/api/records/${id}`)),
  createRecord: (payload) => data(axios.post('/api/records', { type: 'vehicle', ...payload })),
  updateRecord: (id, patch) => data(axios.patch(`/api/records/${id}`, patch)),
  listOpportunities: () => data(axios.get('/api/opportunities')),
  getOpportunity: (id) => data(axios.get(`/api/opportunities/${id}`)),
  createOpportunity: (payload) => data(axios.post('/api/opportunities', payload)),
  searchCatalog: (params = {}) => data(axios.get('/api/catalog', {
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
  })),
  moveOpportunity: (id, stage) => data(axios.patch(`/api/opportunities/${id}/stage`, { stage })),
  getLineWizard: () => data(axios.get('/api/linebot/wizard')),
  saveLineWizard: (step, payload) => data(axios.put(`/api/linebot/wizard/steps/${step}`, payload)),
  probeLineWebhook: () => data(axios.post('/api/linebot/wizard/probe')),
  getLineTestStatus: () => data(axios.get('/api/linebot/wizard/test-status')),
  enableLineBot: () => data(axios.post('/api/linebot/wizard/enable')),
  getAssistantStatus: () => data(axios.get('/api/assistant/status')),
  sendAssistantMessage: (text, conversationId) => data(axios.post(
    '/api/assistant/messages',
    { text, conversation_id: conversationId },
    { timeout: 280000 }
  )),
  getOrgOverview: () => data(axios.get('/api/org/overview')),
  getPlatformMe: () => data(axios.get('/api/platform/me')),
  listPlatformCompanies: (params = {}) => data(axios.get('/api/platform/companies', {
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
  })),
  getPlatformCompany: (id) => data(axios.get(`/api/platform/companies/${id}`)),
  createPlatformCompany: (payload) => data(axios.post('/api/platform/companies', payload)),
  updatePlatformCompany: (id, payload) => data(axios.patch(`/api/platform/companies/${id}`, payload)),
  listPlatformCompanyMembers: (id, params = {}) => data(axios.get(`/api/platform/companies/${id}/members`, {
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
  })),
  addPlatformCompanyMember: (id, payload) => data(axios.post(`/api/platform/companies/${id}/members`, payload)),
  updatePlatformCompanyMember: (companyId, userId, payload) => data(axios.patch(`/api/platform/companies/${companyId}/members/${userId}`, payload)),
  setPlatformCompanyMemberStatus: (companyId, userId, is_active) => data(axios.patch(`/api/platform/companies/${companyId}/members/${userId}/status`, { is_active })),
  updateDraft: (id, changes) => data(axios.put(`/api/assistant/drafts/${id}`, { changes })),
  confirmDraft: (id) => data(axios.post(`/api/assistant/drafts/${id}/confirm`)),
  discardDraft: (id) => data(axios.post(`/api/assistant/drafts/${id}/discard`)),
  estimateRealtyTax: (payload) => data(axios.post('/api/velopulse/realty/tax-estimate', payload)),
  listRealtyListings: (params = {}) => data(axios.get('/api/velopulse/realty/listings', {
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
  })),
  getRealtyListing: (id) => data(axios.get(`/api/velopulse/realty/listings/${id}`)),
  downloadRealtyImportTemplate: () => axios.get('/api/velopulse/realty/listings/import-template', { responseType: 'blob' }),
  importRealtyListings: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return data(axios.post('/api/velopulse/realty/listings/import', fd));
  },
  createRealtyListing: (payload) => data(axios.post('/api/velopulse/realty/listings', payload)),
  addRealtyListingPhoto: (id, { url, file } = {}) => {
    if (file) {
      const fd = new FormData();
      fd.append('image', file);
      return data(axios.post(`/api/velopulse/realty/listings/${id}/photos`, fd));
    }
    return data(axios.post(`/api/velopulse/realty/listings/${id}/photos`, { url }));
  },
  deleteRealtyListingPhoto: (id, photoId) => data(axios.delete(`/api/velopulse/realty/listings/${id}/photos/${photoId}`)),
  uploadImage: (file) => {
    const fd = new FormData();
    fd.append('image', file);
    return data(axios.post('/api/uploads/image', fd));
  },
  getPlateMeta: () => data(axios.get('/api/velopulse/auto/plate/meta')),
  lookupPlate: (payload) => data(axios.post('/api/velopulse/auto/plate/lookup', payload)),
  lookupTitleDeed: (payload) => data(axios.post('/api/velopulse/realty/title-deed/lookup', payload)),
  listOrgMembers: (params = {}) => data(axios.get('/api/org/members', {
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
  })),
  updateOrgMember: (id, payload) => data(axios.patch(`/api/org/members/${id}`, payload)),
  setOrgMemberStatus: (id, is_active) => data(axios.patch(`/api/org/members/${id}/status`, { is_active })),
  toggleOrgMember: (id) => data(axios.patch(`/api/org/members/${id}/status`)),
  getOrgProfile: () => data(axios.get('/api/org/profile')),
  updateOrgProfile: (payload) => data(axios.patch('/api/org/profile', payload)),
  listOrgInvites: (params = {}) => data(axios.get('/api/org/invites', {
    params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null)),
  })),
  createOrgInvite: (payload) => data(axios.post('/api/org/invites', payload)),
  sendOrgInvite: (id) => data(axios.post(`/api/org/invites/${id}/send`)),
  resendOrgInvite: (id) => data(axios.post(`/api/org/invites/${id}/resend`)),
  revokeOrgInvite: (id) => data(axios.post(`/api/org/invites/${id}/revoke`)),
  previewInvite: (token) => data(axios.get(`/api/invites/${token}`)),
  acceptInvite: (token, payload = {}) => data(axios.post(`/api/invites/${token}/accept`, payload)),
};

export const INVITE_STATUS_LABEL = {
  draft: '草稿',
  sent: '已寄出',
  failed: '寄送失敗',
  pending: '待接受',
  accepted: '已接受',
  expired: '已過期',
  revoked: '已撤銷',
};

export const vpApi = FIXTURES_ON ? fixtures : live;

// ── 顯示用常數（系統鍵不直接出現在 UI）──
export const VEHICLE_STATUS = {
  available: { label: '上架中', tone: 'green' },
  preparing: { label: '整備中', tone: '' },
  reserved: { label: '預約賞車', tone: 'indigo' },
  demo: { label: '試乘車', tone: 'blue' },
  sold: { label: '已售出', tone: 'gray' },
  unavailable: { label: '已下架', tone: 'gray' },
};

export const OPP_STAGES = [
  { key: 'new', label: '新進線', tone: '' },
  { key: 'negotiating', label: '洽談中', tone: 'blue' },
  { key: 'contract', label: '待簽約', tone: 'green' },
  { key: 'won', label: '已成交', tone: 'green' },
  { key: 'closed', label: '已關閉', tone: 'gray' },
];
export const stageOf = (key) => OPP_STAGES.find((s) => s.key === key) || { key, label: '未分類', tone: 'gray' };

export const fmtMoney = (n) => (n == null || n === '' ? '—' : `NT$ ${Number(n).toLocaleString('en-US')}`);
export const fmtWan = (n) => (n == null || n === '' ? '—' : `NT$ ${Math.round(Number(n) / 10000).toLocaleString('en-US')} 萬`);
export const fmtNum = (n) => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-US'));

export function fmtRelDay(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(new Date()) - startOf(d)) / 86400000);
  if (diff === 0) return '今天';
  if (diff === 1) return '昨天';
  if (diff > 1 && diff < 7) return `${diff} 天前`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (x) => String(x).padStart(2, '0');
  return `${fmtRelDay(iso)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const vehicleTitle = (r) => [r.brand, r.model].filter(Boolean).join(' ') || r.model_name || '未命名車輛';
